/** @format */

import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, switchMap, map, catchError, mergeMap, from, withLatestFrom, forkJoin, concatMap, concat, toArray } from 'rxjs';
import {
	DelegationEntry,
	GetAllPayPeriodData,
	ManagerAdDirectReports,
	NewUser,
	NotificationStates,
	PayPeriod,
	ScheduledActingAssignment,
	TeamMemberProfile,
	TimeentryDB,
	Timesheet,
	User,
	UserNotification,
	UserTimesheet,
} from '.';

import {
	AddSingleUser,
	GetProfile,
	GetProfilePicture,
	SetProfilePicture,
	SaveTimeEntries,
	SubmitTimeEntries,
	PostTimeEntriesSuccess,
	PostTimeEntriesError,
	GetProfileSuccess,
	LoadStaticData,
	LoadStaticDataSuccess,
	LoadStaticDataError,
	GetTimeBanks,
	GetTimeBanksSuccess,
	GetTimeBanksError,
	ToastUserNotification,
	GetNotifications,
	GetNotificationsSuccess,
	GetNotificationsError,
	UpdateNotification,
	UpdateNotificationSuccess,
	UpdateNotificationError,
	SaveTimesheet,
	PostTimesheetError,
	SubmitTimesheet,
	GetAllTimesheets,
	GetAllTimesheetsSuccess,
	GetAllTimesheetsError,
	UpdateNotificationActive,
	ApproveTimesheets,
	GetEmployeeTimesheetsSuccess,
	GetEmployeeTimesheets,
	GetAllPayPeriodDataSuccess,
	GetAllPayPeriodDataError,
	StartApplication,
	AttemptApplicationStart,
	GetScheduledActingAssignments,
	GetScheduledActingAssignmentsError,
	GetScheduledActingAssignmentsSuccess,
	GetAllScheduledActingAssignments,
	GetAllScheduledActingAssignmentsSuccess,
	GetAllScheduledActingAssignmentsError,
	AddScheduledActingAssignment,
	UpdateScheduledActingAssignment,
	GetApprovedTimesheetsSuccess,
	SetApprovedTeamProfile,
	GetTimesheetHistory,
	GetTimesheetHistorySuccess,
	GetTimesheetHistoryError,
	GetUserDelegations,
	GetUserDelegationsSuccess,
	AddDelegation,
	UpdateDelegation,
	DeleteDelegation,
	DeleteScheduledActingAssignment,
	UpdateTimesheetApproval,
	AddUserImages,
	GetTeamProfilePicsBulk,
	SetTeamProfileBulk,
	GetUserRelatedActingAssignments,
	SaveMemberProfile,
	SaveMemberProfileSuccess,
	SaveMemberProfileError,
	GetApprovedTimesheets,
	GetTimeEntries,
	GetTimeEntriesSuccess,
	GetTimeEntriesError,
	UpdateMemberProfile,
	UpdateMemberProfileSuccess,
	UpdateMemberProfileError,
	GetTimekeeperRelatedDelegations,
	PruneManagedActiveUsers,
	PruneManagedActiveUsersError,
	PruneManagedActiveUsersSuccess,
	SetUsersInactive,
	SetUsersInactiveSuccess,
	SetUsersInactiveError,
	CloseTimeEntries,
	GenerateUserProfile,
	GetAllUsers,
	SetAllUsers,
	CreateNewUser,
	UpdateExistingUser,
} from './application.action';

import {
	UserService,
	ProfileService,
	EntryService,
	StatService,
	StaticService,
	TimebankService,
	NotificationsService,
	TimesheetService,
	PayPeriodService,
	ActingService,
	DelegationsService,
} from 'src/app/services';
import { State } from 'src/app/store';

import { MsalService } from '@azure/msal-angular';
import { TimeBank } from 'src/app/models/timeBank';
import { Toast, ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubmitSuccessModalComponent } from 'src/app/components/submit-success-modal/submit-success-modal.component';
import { EmailNotification } from 'src/app/models/notifications';
import { DomSanitizer } from '@angular/platform-browser';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';
import { SafeUrl } from '@angular/platform-browser';
import { ManageProfile } from 'src/app/models/profile';
import { roles } from 'src/app/app.module';

@Injectable()
export class ApplicationEffects {
	getAllPayPeriodData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetAllPayPeriodData),
			withLatestFrom(this.store$),
			switchMap(() => {
				return this.payPeriodService.getAllPayPeriods().pipe(
					switchMap((response: PayPeriod[]) => {
						const payPeriods: PayPeriod[] = [];
						response.forEach((pp) => {
							payPeriods.push({
								...pp,
								startDate: new Date(pp.startDate),
								endDate: new Date(pp.endDate),
								timeEntryCutOffDate: new Date(pp.timeEntryCutOffDate),
								payDate: new Date(pp.payDate),
							});
						});

						const actions: Action[] = [GetAllPayPeriodDataSuccess({ payPeriods }), AttemptApplicationStart()];
						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'PayPeriod retrieval failure',
							message: `PayPeriods failed to be retrieved. Please try again.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetAllPayPeriodDataError({ error })];

						return actions;
					})
				);
			})
		)
	);

	attemptApplicationStart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AttemptApplicationStart),
			withLatestFrom(this.store$),
			switchMap(([action, store]) => {
				const actions: Action[] = [];
				const state = store.applicationReducer;

				const payPeriodsLoaded = state.initialLoadingState.isPayPeriodsLoaded;
				const statHolidaysLoaded = state.initialLoadingState.isStatHolidaysLoaded;
				const profileLoaded = state.initialLoadingState.isProfileLoaded;
				const timesheetsLoaded = state.initialLoadingState.isTimesheetsLoaded;
				const scheduledActingsLoaded = state.initialLoadingState.isScheduledActingsLoaded;

				if (payPeriodsLoaded && statHolidaysLoaded && profileLoaded && timesheetsLoaded && scheduledActingsLoaded) {
					actions.push(StartApplication());
				}

				return actions;
			})
		)
	);

	addScheduledActingAssignment$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AddScheduledActingAssignment),
			switchMap((action) => {
				return this.actingService.addScheduledActingAssignment(action.scheduledActingAssignment).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Scheduled acting assignment added successfully',
							message: `The acting assignments was successfully saved.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetUserRelatedActingAssignments()];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Failed to add new scheduled acting assignment',
							message: error.error.title ? error.error.title : error.error,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	updateScheduledActingAssignment$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateScheduledActingAssignment),
			switchMap((action) => {
				return this.actingService.updateScheduledActingAssignment(action.scheduledActingAssignment).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Scheduled acting assignment updated successfully',
							message: `The acting assignments was successfully updated.`,
						};

						let actions: Action[] = [ToastUserNotification({ notification }), GetAllScheduledActingAssignments()];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Failed to update scheduled acting assignment',
							message: error.error.title ? error.error.title : error.error,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	deleteScheduledActingAssignment$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DeleteScheduledActingAssignment),
			switchMap((action) => {
				return this.actingService.deleteScheduledActingAssignment(action.id).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Scheduled acting assignment deleted successfully',
							message: `The acting assignments was successfully deleted.`,
						};

						let actions: Action[] = [ToastUserNotification({ notification }), GetAllScheduledActingAssignments()];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Failed to delete scheduled acting assignment',
							message: `Scheduled acting assignment failed to delete. Please try again.`,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	getUserRelatedActingAssignments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetUserRelatedActingAssignments),
			switchMap(() => {
				return this.actingService.getUserRelatedActingAssignments().pipe(
					switchMap((response: ScheduledActingAssignment[]) => {
						let scheduledActingAssignments: ScheduledActingAssignment[] = [];
						response.forEach((actingAssignment) => {
							scheduledActingAssignments.push({
								...actingAssignment,
								from: new Date(actingAssignment.from),
								to: new Date(actingAssignment.to),
							});
						});

						const actions: Action[] = [
							GetScheduledActingAssignmentsSuccess({
								scheduledActingAssignments,
							}),
						];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Scheduled acting assignments retrieval failure',
							message: `Scheduled acting assignments failed to be retrieved. Please try again.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetScheduledActingAssignmentsError({ error })];

						return actions;
					})
				);
			})
		)
	);

	getAllScheduledActingAssignments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetAllScheduledActingAssignments),
			switchMap(() => {
				return this.actingService.getAllScheduledActingAssignments().pipe(
					switchMap((response: ScheduledActingAssignment[]) => {
						let scheduledActingAssignments: ScheduledActingAssignment[] = [];
						response.forEach((actingAssignment) => {
							scheduledActingAssignments.push({
								...actingAssignment,
								from: new Date(actingAssignment.from),
								to: new Date(actingAssignment.to),
							});
						});

						const actions: Action[] = [
							GetAllScheduledActingAssignmentsSuccess({
								scheduledActingAssignments,
							}),
						];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Scheduled acting assignments retrieval failure',
							message: `Scheduled acting assignments failed to be retrieved. Please try again.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetAllScheduledActingAssignmentsError({ error })];

						return actions;
					})
				);
			})
		)
	);

	getScheduledActingAssignments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetScheduledActingAssignments),
			switchMap(() => {
				return this.actingService.getUserScheduledActingAssignments().pipe(
					switchMap((response: ScheduledActingAssignment[]) => {
						let scheduledActingAssignments: ScheduledActingAssignment[] = [];
						response.forEach((actingAssignment) => {
							scheduledActingAssignments.push({
								...actingAssignment,
								from: new Date(actingAssignment.from),
								to: new Date(actingAssignment.to),
							});
						});

						const actions: Action[] = [
							GetScheduledActingAssignmentsSuccess({
								scheduledActingAssignments,
							}),
							AttemptApplicationStart(),
						];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Scheduled acting assignments retrieval failure',
							message: `Scheduled acting assignments failed to be retrieved. Please try again.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetScheduledActingAssignmentsError({ error })];

						return actions;
					})
				);
			})
		)
	);

	// getAllPayPeriodData$ = createEffect(
	//   () => this.actions$.pipe(
	//     ofType(GetAllPayPeriodData),
	//     withLatestFrom(this.store$),
	//     switchMap(([action, store]) => {
	//       return this.entryService.getTimeEntries().pipe(
	//         switchMap((response: any) => {
	//           return of(GenerateCalendarData({ payPeriodEntries: response }));
	//         }),
	//         catchError((error: any) => {
	//           console.log(error.message);
	//           return of();
	//         })
	//       );
	//     })
	//   )
	// );

	getAllUsers = createEffect(() =>
		this.actions$.pipe(
			ofType(GetAllUsers),
			switchMap(() => {
				return this.userService.getUsers().pipe(
					switchMap((allUsers) => {
						return of(SetAllUsers({ allUsers }));
					}),
					catchError((err) => {
						console.error(err);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Failed to get all users',
							message: `Failed to extract all users from the database. Please contact the PADS team to take a look if the issue persists after a refresh.`,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	getProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetProfile),
			switchMap((action) => {
				// check if the user needs to be activated first
				let activeAccounts = this.authService.instance.getAllAccounts();
				let activeAccount = activeAccounts.length > 0 ? activeAccounts[0] : null;
				if (activeAccount?.idTokenClaims?.roles?.includes(roles.AppAccess) && !action.skipUserActivation) {
					// activate user
					return this.userService.activateCurrentUser().pipe(
						switchMap(() => {
							return of(GetProfile({ skipUserActivation: true }));
						}),
						catchError((error) => {
							console.log(error);
							const notification: UserNotification = {
								type: NotificationStates.Error,
								title: 'Account activation error',
								message: `Failed to activate your account. Please contact your manager to place a request for a manual profile activation.`,
							};

							return of(ToastUserNotification({ notification }));
						})
					);
				}

				return this.profileService.getProfile().pipe(
					switchMap((profileData) => {
						const actions: Action[] = [GetProfileSuccess({ profile: profileData }), AttemptApplicationStart()];
						return actions;
					}),
					catchError((error) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Warning,
							title: 'User profile does not exist',
							message: `Your account does not exist. Please wait while one gets generated for you.`,
						};

						return [ToastUserNotification({ notification }), GenerateUserProfile()];
					})
				);
			})
		)
	);

	getProfileSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetProfileSuccess),
			switchMap(() => {
				return of(GetProfilePicture());
			})
		)
	);

	getProfilePicture$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetProfilePicture),
			switchMap(() => {
				const account = (
					this.authService.instance.getActiveAccount()
						? this.authService.instance.getActiveAccount()
						: this.authService.instance.getAllAccounts().filter((a) => !a.username.match(/adm@/i))[0]
				)!;
				const request = {
					scopes: ['User.Read'],
					account,
				};

				return from(this.authService.instance.acquireTokenSilent(request));
			}),
			switchMap((token: any) => {
				return from(this.profileService.getProfilePicture(token.accessToken));
			}),
			switchMap((response: any) => {
				return of(SetProfilePicture({ pictureBlob: response }));
			}),
			catchError((error: any) => {
				console.log(error);
				return of(SetProfilePicture({ pictureBlob: null }));
			})
		)
	);

	pruneManagedActiveUsers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PruneManagedActiveUsers),
			switchMap((action) => {
				return from(this.profileService.pruneManagedUsers());
			}),
			switchMap((response: any) => {
				return of(PruneManagedActiveUsersSuccess({ users: response }));
			}),
			// switchMap((action) => {
			// 	const users = [...action.users];
			// 	const account = (
			// 		this.authService.instance.getActiveAccount()
			// 			? this.authService.instance.getActiveAccount()
			// 			: this.authService.instance.getAllAccounts().filter((a) => !a.username.match(/adm@/i))[0]
			// 	)!;
			// 	const request = { scopes: ['User.Read', 'Profile'], account };

			// 	return from(this.authService.instance.acquireTokenSilent(request)).pipe(
			// 		map((token: any) => {
			// 			return { token, users };
			// 		})
			// 	);
			// }),

			// switchMap(({ token, users }) => {
			// 	let upns = users.map((user) => user.upn);
			// 	return forkJoin({
			// 		users: of(users),
			// 		result: forkJoin(
			// 			upns.map((upn) => {
			// 				return this.profileService.getUserExists(upn, token.accessToken);
			// 			})
			// 		),
			// 	});
			// }),

			// switchMap((response: any) => {
			// 	let usersToInactivate: User[] = [];
			// 	response.users.forEach((user: User, index: number) => {
			// 		let userRecord = response.result[index];
			// 		let userExists = userRecord.value.length > 0;
			// 		let userAccountValue = userExists && userRecord.value[0];
			// 		let isUserAccountEnabled = userExists && (userAccountValue.accountEnabled !== null ? userAccountValue.accountEnabled : true);

			// 		if (!isUserAccountEnabled) {
			// 			usersToInactivate.push(user);
			// 		}
			// 	});

			// 	// console.log(usersToInactivate);
			// 	return of(PruneManagedActiveUsersSuccess({ users: usersToInactivate }));
			// }),

			catchError((error: any) => {
				console.log(error);
				return of(PruneManagedActiveUsersError({ error }));
			})
		)
	);

	pruneManagedActiveUsersSucess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PruneManagedActiveUsersSuccess),
			switchMap((action) => {
				const usersToInactivate = action.users;
				const userIds: number[] = [];
				usersToInactivate.forEach((user) => userIds.push(user.id!));

				if (userIds.length > 0) return of(SetUsersInactive({ userIds }));
				return [];
			})
		)
	);

	setUsersInactive$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SetUsersInactive),
			switchMap((action) => {
				return this.profileService.setUsersInactive(action.userIds).pipe(
					switchMap((result: User[]) => {
						let userNames: string[] = [];
						result.forEach((user) => userNames.push(`${user.firstName} ${user.lastName}`));
						return of(SetUsersInactiveSuccess({ userNames }));
					}),
					catchError((error: any) => {
						console.log(error);
						return of(SetUsersInactiveError({ error }));
					})
				);
			})
		)
	);

	setUsersInactiveSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SetUsersInactiveSuccess),
			switchMap((action) => {
				let userNames = action.userNames;
				let singleUser = userNames.length === 1;
				let userNameString = '';
				userNames.forEach((name: string, index: number) => {
					if (index < userNames.length - 2) userNameString += `${name}, `;
					else if (index === userNames.length - 2) {
						if (userNames.length == 2) userNameString += `${name} and `;
						else userNameString += `${name}, and `;
					} else userNameString += `${name}`;
				});

				let message = `${singleUser ? 'User' : 'Users'} ${userNameString} ${
					singleUser ? 'has' : 'have'
				} been automatically disabled as they are no longer ${singleUser ? 'an employee' : 'employees'} with the district.`;

				const notification: UserNotification = {
					type: NotificationStates.Success,
					title: 'Inactive users automatically deactivated',
					message,
				};

				const actions: Action[] = [ToastUserNotification({ notification }), GetProfile({ skipUserActivation: true })];

				return actions;
			})
		)
	);

	setUsersInactiveError$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SetUsersInactiveError),
			switchMap((action) => {
				const notification: UserNotification = {
					type: NotificationStates.Error,
					title: 'Inactive user pruning failed',
					message: 'Failed to automatically disable inactive users.',
				};

				const actions: Action[] = [ToastUserNotification({ notification })];

				return actions;
			})
		)
	);

	getTeamProfilePicBulk$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetTeamProfilePicsBulk),
			withLatestFrom(this.store$),
			switchMap(([action, store]) => {
				const requiredUserImageUpn = action.upns;

				// mergeMap((action: any) => {
				const account = (
					this.authService.instance.getActiveAccount()
						? this.authService.instance.getActiveAccount()
						: this.authService.instance.getAllAccounts().filter((a) => !a.username.match(/adm@/i))[0]
				)!;
				const request = {
					scopes: ['User.Read'],
					account,
				};

				return from(this.authService.instance.acquireTokenSilent(request)).pipe(
					map((token: any) => {
						return {
							token,
							requiredUserImageUpn,
							store,
						};
					})
				);
			}),
			switchMap(({ token, requiredUserImageUpn, store }) => {
				return forkJoin({
					blobs: forkJoin(
						requiredUserImageUpn.map((upn: string) => {
							if (upn.toLowerCase().indexOf('automatedtesting') > -1) {
								const atIdx = upn.indexOf('@');
								const tester = upn.substring(0, atIdx);
								return of(`/assets/images/${tester}.jpg`);
							} else if (upn.toLowerCase() === 'timesheet@dnv.org') {
								return of('/assets/images/SystemAccount.png');
							} else if (store.applicationReducer.userImages[upn]) {
								return of(store.applicationReducer.userImages[upn]);
							} else {
								return this.profileService.getTeamProfilePicObservable(upn, token.accessToken).pipe(
									catchError(() => {
										return of(`/assets/images/blankUser.jpg`);
									})
								);
							}
						})
					),
					requiredUserImageUpn: of(requiredUserImageUpn),
				});
			}),

			switchMap((response: any) => {
				// console.log(response);
				// const base64Img = this.convertBlobToBase64(response.teamPic);
				const blobMap: TeamMemberProfile[] = [];

				response.requiredUserImageUpn.forEach((upn: string, index: number) => {
					const b: Blob | undefined = response.blobs[index];
					if (b && !upn.toLowerCase().includes('automatedtesting') && typeof b !== 'string' && b instanceof Blob) {
						const urlCreator = window.URL || window.webkitURL;
						const imgUrl = this.domSanitizer.bypassSecurityTrustUrl(urlCreator.createObjectURL(b));
						blobMap.push({ email: upn, profilePic: imgUrl });
					} else {
						blobMap.push({ email: upn, profilePic: b as any as SafeUrl });
					}
				});

				return of(SetTeamProfileBulk({ userImages: blobMap }));
			}),
			catchError((error: any, action: any) => {
				console.log(error);
				// 39 for where email starts after '.../users/...'
				// const slashIdx = error.url.substring(39).indexOf('/');
				// const email = error.url.substring(39).substring(0, slashIdx);
				return of(SetTeamProfileBulk({ userImages: [] }));
			})
		)
	);

	//Test effect
	accessDeniedAddUser$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AddSingleUser),
				switchMap((action) => {
					return this.userService.addSingleUser(action.newUser).pipe(
						map(() => {
							const notification: UserNotification = {
								type: NotificationStates.Info,
								title: 'Contact Helpdesk',
								message: `You must be assigned a role to access the application. Please try again after contacting helpdesk to sort out the problem.`,
							};

							return of(ToastUserNotification({ notification }));
						}),
						catchError((error: any) => {
							console.error(error);

							const notification: UserNotification = {
								type: NotificationStates.Error,
								title: 'Contact IT Projects',
								message: `There was an error creating your profile. Please contact a developer from the IT Projects team under Kent Fong.`,
							};

							return of(ToastUserNotification({ notification }));
						})
					);
				})
			),
		{ dispatch: false }
	);

	getAllTimesheets$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetAllTimesheets),
			switchMap(() => {
				return this.timesheetService.getTimesheets().pipe(
					switchMap((response: Timesheet[]) => {
						const timesheets: Timesheet[] = response.map((t) => HelperFunctions.parseLoadedTimesheet(t));
						// response.forEach(timesheet => {
						//   timesheets.push({
						//     startDate: new Date(timesheet.startDate),
						//     endDate: new Date(timesheet.endDate),
						//     payPeriod: timesheet.payPeriod,
						//     payDate: timesheet.payDate,
						//     payPeriodStartDate: timesheet.payPeriodStartDate,
						//     payPeriodIndex: timesheet.payPeriodIndex,
						//     status: timesheet.status,
						//     metadata: JSON.parse(timesheet.metadata.toString()),
						//     entries: JSON.parse(timesheet.entries)
						//   })
						// })

						const actions: Action[] = [GetAllTimesheetsSuccess({ timesheets }), AttemptApplicationStart()];
						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Timesheet retrieval failure',
							message: `Timesheet failed to be retrieved. Please try again.`,
						};

						// const actions: Action[] = [
						//   GetAllTimesheetsError({ error }),
						//   ToastUserNotification({ notification })
						// ];

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	getTimekeeperRelatedDelegations$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetTimekeeperRelatedDelegations),
			switchMap(() => {
				return this.delegationService.getTimekeeperDelegations().pipe(
					switchMap((response: DelegationEntry[]) => {
						let delegations: DelegationEntry[] = [];
						response.forEach((delegation) => {
							delegations.push({
								...delegation,
								from: new Date(delegation.from),
								to: new Date(delegation.to),
							});
						});

						return of(GetUserDelegationsSuccess({ delegations }));
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Delegations retrieval failure',
							message: `User delegations failed to be retrieved. Please try again.`,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	getUserDelegations$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetUserDelegations),
			switchMap(() => {
				return this.delegationService.getUserDelegations().pipe(
					switchMap((response: DelegationEntry[]) => {
						let delegations: DelegationEntry[] = [];
						response.forEach((delegation) => {
							delegations.push({
								...delegation,
								from: new Date(delegation.from),
								to: new Date(delegation.to),
							});
						});

						return of(GetUserDelegationsSuccess({ delegations }));
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Delegations retrieval failure',
							message: `User delegations failed to be retrieved. Please try again.`,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	addDelegation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AddDelegation),
			switchMap((action) => {
				return this.delegationService.addDelegation(action.delegation).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Delegation adding success',
							message: `Delegation successfully added.`,
						};

						const actions: Action[] = [
							ToastUserNotification({ notification }),
							action.isTimekeeper ? GetTimekeeperRelatedDelegations() : GetUserDelegations(),
						];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Delegation adding failure',
							message: error.error.title ? error.error.title : error.error,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	updateDelegation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateDelegation),
			switchMap((action) => {
				return this.delegationService.updateDelegation(action.delegation).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Delegation update success',
							message: `Delegation successfully updated.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetUserDelegations()];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Delegation update failure',
							message: error.error.title ? error.error.title : error.error,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	deleteDelegation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DeleteDelegation),
			switchMap((action) => {
				return this.delegationService.deleteDelegation(action.id).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Delegation delete success',
							message: `Delegation successfully deleted.`,
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetUserDelegations()];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Delegation delete failure',
							message: `Failed to delete the delegation. Please try again.`,
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	getApprovedTimesheets$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetApprovedTimesheets),
			switchMap((action) => {
				return this.timesheetService.getApprovedTimesheets(action.allMonthTimesheets, action.isManagerView).pipe(
					switchMap((response: UserTimesheet[]) => {
						const timesheets: UserTimesheet[] = [];
						response.forEach((uts) => {
							timesheets.push({
								userId: uts.userId,
								timekeeperId: uts.timekeeperId,
								active: uts.active,
								employeeEmail: uts.employeeEmail,
								employeeFirstName: uts.employeeFirstName,
								employeeLastName: uts.employeeLastName,
								employeeNumber: uts.employeeNumber,
								configurations: uts.configurations,
								timesheets: uts.timesheets.map((t) => HelperFunctions.parseLoadedTimesheet(t)),
							});
						});

						return from([timesheets]);
					}),
					switchMap((timesheets: UserTimesheet[]) => {
						return this.timesheetService.getAllUserTimesheets().pipe(
							switchMap((response: UserTimesheet[]) => {
								const allTimesheets: UserTimesheet[] = [];
								response.forEach((uts) => {
									allTimesheets.push({
										userId: uts.userId,
										timekeeperId: uts.timekeeperId,
										active: uts.active,
										employeeEmail: uts.employeeEmail,
										employeeFirstName: uts.employeeFirstName,
										employeeLastName: uts.employeeLastName,
										employeeNumber: uts.employeeNumber,
										configurations: uts.configurations,
										timesheets: uts.timesheets.map((t) => HelperFunctions.parseLoadedTimesheet(t)),
										timesheetsRolledBack: !!uts.timesheetsRolledBack
											? uts.timesheetsRolledBack!.map((t) => HelperFunctions.parseLoadedTimesheet(t))
											: [],
									});
								});

								return forkJoin([of(timesheets), of(allTimesheets)]);
							})
						);
					}),
					switchMap((timesheetData: any[]) => {
						return this.actingService.getAllScheduledActingAssignments().pipe(
							switchMap((response: ScheduledActingAssignment[]) => {
								let actingAssignments: ScheduledActingAssignment[] = [];
								response.forEach((actingAssignment) => {
									actingAssignments.push({
										...actingAssignment,
										from: new Date(actingAssignment.from),
										to: new Date(actingAssignment.to),
									});
								});

								const timesheets: UserTimesheet[] = timesheetData[0];
								const allTimesheets: UserTimesheet[] = timesheetData[1];

								// console.log(timesheets);
								// console.log(allTimesheets);

								const actions: Action[] = [
									GetApprovedTimesheetsSuccess({
										timesheets,
										allTimesheets,
										actingAssignments,
									}),
								];
								return actions;
							})
						);
					}),
					catchError((error: any) => {
						console.log(error);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Approved Timesheet retrieval failure',
							message: `Approved Timesheet failed to be retrieved. Please try again.`,
						};
						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	saveTimesheet$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SaveTimesheet),
			switchMap((action) => {
				return this.timesheetService
					.postTimesheet(action.startDate, {
						...action.timesheet,
						action: 'Save',
					})
					.pipe(
						switchMap(() => {
							const notification: UserNotification = {
								type: NotificationStates.Success,
								title: 'Timesheet saved',
								message: `Timesheet for ${action.weekRange} has been saved`,
							};

							const actions: Action[] = [ToastUserNotification({ notification }), GetAllTimesheets()];

							if (action.isManager) {
								actions.push(GetEmployeeTimesheets());
							}

							return actions;
						}),
						catchError((error: any) => {
							console.log(error.message);
							const notification: UserNotification = {
								type: NotificationStates.Error,
								title: 'Timesheet save failure',
								message: `Timesheet for ${action.weekRange} encountered an error when attempting to save`,
							};

							const actions: Action[] = [PostTimesheetError({ action, error }), ToastUserNotification({ notification })];

							return actions;
						})
					);
			})
		)
	);

	submitTimesheet$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SubmitTimesheet),
			mergeMap((action) => {
				return this.timesheetService
					.postTimesheet(action.startDate, {
						...action.timesheet,
						action: action.timesheet.action === 'approve' ? 'Approve' : 'Submit',
					})
					.pipe(
						switchMap(() => {
							let notification: UserNotification | undefined;
							const actions: Action[] = [];

							if (action.timesheet.action == 'approve') {
								notification = {
									type: NotificationStates.Success,
									title: 'Timesheet approved',
									message: `Timesheet for ${action.weekRange} has been approved`,
								};
								actions.push(GetEmployeeTimesheets());
							} else {
								notification = {
									type: NotificationStates.Success,
									title: 'Timesheet submitted',
									message: `Timesheet for ${action.weekRange} has been submitted`,
								};
								actions.push(GetAllTimesheets());
							}

							if (notification) {
								actions.push(ToastUserNotification({ notification }));
							}

							return actions;
						}),
						catchError((error: any) => {
							console.log(error.message);
							let notification: UserNotification;
							if (action.timesheet.action == 'approve') {
								notification = {
									type: NotificationStates.Error,
									title: 'Timesheet approval failure',
									message: action.weekRange
										? `Predicted timesheet for ${action.weekRange} encountered an error when attempting to approve`
										: 'Encountered an error approving predicted timesheets',
								};
							} else {
								notification = {
									type: NotificationStates.Error,
									title: 'Timesheet save failure',
									message: `Timesheet for ${action.weekRange} encountered an error when attempting to submit`,
								};
							}

							const actions: Action[] = [PostTimesheetError({ action, error }), ToastUserNotification({ notification })];

							return actions;
						})
					);
			})
		)
	);

	// saveTimeEntries$ = createEffect(
	//   () => this.actions$.pipe(
	//     ofType(SaveTimeEntries),
	//     withLatestFrom(this.store$),
	//     switchMap(([action, store]) => {
	//       const yyyyppww: string = action.yyyyppww;
	//       return this.entryService.postTimeEntries(yyyyppww, { action: 'Save', timeEntries: action.timeEntries }).pipe(
	//         switchMap((response: any) => {
	//           const notification: UserNotification = {
	//             type: NotificationStates.Success,
	//             title: "Time entries saved",
	//             message: `Time entries from ${action.weekRange} have been saved`
	//           }

	//           const actions: Action[] = [
	//             PostTimeEntriesSuccess({ action, response }),
	//             ToastUserNotification({ notification }),
	//             GetAllPayPeriodData()
	//           ];

	//           return actions;
	//         }),
	//         catchError((error: any) => {
	//           console.log(error.message);
	//           const notification: UserNotification = {
	//             type: NotificationStates.Error,
	//             title: "Time entries save failure",
	//             message: `Time entries from ${action.weekRange} encountered an error when attempting to save`
	//           }

	//           const actions: Action[] = [
	//             PostTimeEntriesError({ action, error }),
	//             ToastUserNotification({ notification })
	//           ];

	//           return actions;
	//         })
	//       );
	//     })
	//   )
	// );

	submitTimeEntries$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SubmitTimeEntries),
			switchMap((action) => {
				// todo refactor
				const approvedTimesheetsIds = action.approvedTimesheetsIds;
				const timeEntries = action.timeEntries;
				const batchNumber = action.batchNumber;

				return this.entryService.postTimeEntries({ approvedTimesheetsIds, timeEntries, batchNumber }).pipe(
					switchMap((response: any) => {
						const batchNumberNotification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Batch number added',
							message: `The batch number you entered has been added to all entries on this page.`,
						};

						return [
							ToastUserNotification({
								notification: batchNumberNotification,
							}),
							GetTimeEntries(),
							GetApprovedTimesheets({
								allMonthTimesheets: action.allMonthTimesheets,
								isManagerView: action.isManagerView,
							}),
						];
					}),
					catchError((error: any) => {
						console.log(error.message);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Time entries were not copied',
							message: `Time entries encountered an error when attempting to copy`,
						};
						const actions: Action[] = [PostTimeEntriesError({ action, error }), ToastUserNotification({ notification })];
						return actions;
					})
				);
			})
		)
	);

	closeTimeEntries$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CloseTimeEntries),
			switchMap((action) => {
				return this.timesheetService.closeTimeEntries().pipe(
					switchMap(() => {
						const batchNumberNotification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Time entry closed',
							message: `The time entries were closed successfully.`,
						};

						return [
							ToastUserNotification({
								notification: batchNumberNotification,
							}),
							GetTimeEntries(),
							GetApprovedTimesheets({
								allMonthTimesheets: action.allMonthTimesheets,
								isManagerView: action.isManagerView,
							}),
						];
					}),
					catchError((error: any) => {
						console.log(error.message);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Failed to close time entry.',
							message: `Postings encountered an error when attempting to close the current pay period`,
						};
						const actions: Action[] = [ToastUserNotification({ notification })];
						return actions;
					})
				);
			})
		)
	);

	getTimeEntries$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetTimeEntries),
			switchMap(() => {
				return this.entryService.getTimeEntries().pipe(
					switchMap((response) => {
						return of(GetTimeEntriesSuccess({ timeEntries: response }));
					})
				);
			}),
			catchError((error: any) => {
				return of(GetTimeEntriesError({ error: error }));
			})
		)
	);

	approveTimesheets$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ApproveTimesheets),
			switchMap((action) => {
				return this.timesheetService.approveEmployeeTimesheets(action.submissions).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Timesheet approved',
							message: action.weekRange
								? `Timesheet for ${action.weekRange} has been approved`
								: 'All selected timesheets have been approved',
						};

						const actions: Action[] = [GetEmployeeTimesheets(), ToastUserNotification({ notification })];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error.message);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Timesheet approval failure',
							message: action.weekRange
								? `Timesheet for ${action.weekRange} encountered an error when attempting to approve`
								: 'Encountered an error when attempting to approve',
						};

						return of(ToastUserNotification({ notification }));
					})
				);
			})
		)
	);

	updateTimesheetApproval$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateTimesheetApproval),
			switchMap((action) =>
				concat(
					// added 'Approve-Edited' action so timesheet gets approved in /timesheets/approvals and not timesheets/{startDate} to ensure correct notification type is sent out
					// applies to 'predicted' timesheets
					this.timesheetService.postTimesheet(action.startDate, {
						...action.timesheet,
						action: action.isEdited && action.isEdited === true ? 'Approve-Edited' : 'Approve',
					}),
					this.timesheetService.approveEmployeeTimesheets(action.approveTimesheetInfo)
				).pipe(
					toArray(),
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Timesheet approved',
							message: action.weekRange
								? `Timesheet for ${action.weekRange} has been approved`
								: 'All selected timesheets have been approved',
						};

						const actions: Action[] = [ToastUserNotification({ notification }), GetAllTimesheets(), GetEmployeeTimesheets()];
						return actions;
					}),
					catchError((error: any) => {
						console.log(error.message);
						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Timesheet approval failure',
							message: action.weekRange
								? `Timesheet for ${action.weekRange} encountered an error when attempting to approve`
								: 'Encountered an error when attempting to approve',
						};

						return of(ToastUserNotification({ notification }));
					})
				)
			)
		)
	);

	getEmployeeTimesheets$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetEmployeeTimesheets),
			switchMap(() => {
				return this.timesheetService.getEmployeeTimesheets().pipe(map((data: any[]) => GetEmployeeTimesheetsSuccess({ submissions: data })));
			}),
			catchError((error: any) => {
				console.log(error);
				return of(GetNotificationsError(error));
			})
		)
	);

	loadStaticData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadStaticData),
			switchMap((action: any) => {
				return forkJoin({
					stat: this.statService.getStatHolidays(),
					static: this.staticService.getStaticData(),
				}).pipe(
					switchMap((response: any) => {
						const actions: Action[] = [LoadStaticDataSuccess({ action, response }), AttemptApplicationStart()];

						return actions;
					}),
					catchError((error: any) => {
						console.log(error.message);

						const notification: UserNotification = {
							type: NotificationStates.Error,
							title: 'Load static data error',
							message: `Static data loading failed. Please refresh and try again.`,
						};

						const actions: Action[] = [LoadStaticDataError({ action, error }), ToastUserNotification({ notification })];

						return actions;
					})
				);
			})
		)
	);

	loadStaticDataSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadStaticDataSuccess),
			switchMap(() => {
				return of(GetProfile({ skipUserActivation: true }));
			})
		)
	);

	dispatchScheduledActingAssignments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadStaticDataSuccess),
			switchMap(() => {
				return of(GetScheduledActingAssignments());
			})
		)
	);

	dispatchTimeBanks$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadStaticDataSuccess),
			switchMap(() => {
				return of(GetTimeBanks());
			})
		)
	);

	dispatchTimesheets$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadStaticDataSuccess),
			switchMap(() => {
				return of(GetAllTimesheets());
			})
		)
	);

	dispatchPayPeriodData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadStaticDataSuccess),
			switchMap(() => {
				return of(GetAllPayPeriodData());
			})
		)
	);

	// GetTimesheetHistory

	getTimesheetHistory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetTimesheetHistory),
			switchMap((action: any) => {
				return this.timesheetService
					.getTimesheetHistory(action.startDate, action.employeeNumber)
					.pipe(map((data: Timesheet[]) => GetTimesheetHistorySuccess({ success: data, action })));
			}),
			catchError((error) => of(GetTimesheetHistoryError(error)))
		)
	);

	createNewUser$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CreateNewUser),
			switchMap((action: any) => {
				return this.userService.createNewUser(action.newUserDetails).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'User account successfully generated',
							message: `Account for ${action.newUserDetails.firstName} ${action.newUserDetails.lastName} has been successfully created.`,
						};

						return [ToastUserNotification({ notification }), GetAllUsers()];
					})
				);
			}),
			catchError((error: any) => {
				console.error(error);
				const notification: UserNotification = {
					type: NotificationStates.Error,
					title: 'User creation failure',
					message: `The user failed to be created. Please check the console errors for more information.`,
				};

				return [ToastUserNotification({ notification })];
			})
		)
	);

	updateExistingUser$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateExistingUser),
			switchMap((action: any) => {
				return this.userService.updateExistingUser(action.updatedUserDetails).pipe(
					switchMap(() => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'User account successfully updated',
							message: `Account for ${action.updatedUserDetails.newAccountDetails.firstName} ${action.updatedUserDetails.newAccountDetails.lastName} has been successfully updated.`,
						};

						return [ToastUserNotification({ notification }), GetAllUsers()];
					})
				);
			}),
			catchError((error: any) => {
				console.error(error);
				const notification: UserNotification = {
					type: NotificationStates.Error,
					title: 'User updated failure',
					message: `The user failed to update. Please check the console errors for more information.`,
				};

				return [ToastUserNotification({ notification })];
			})
		)
	);

	generateUserProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GenerateUserProfile),
			switchMap(() => {
				const account = (
					this.authService.instance.getActiveAccount()
						? this.authService.instance.getActiveAccount()
						: this.authService.instance.getAllAccounts().filter((a) => !a.username.match(/adm@/i))[0]
				)!;

				const request = {
					scopes: ['User.Read', 'Profile'],
					account,
				};

				return from(this.authService.instance.acquireTokenSilent(request)).pipe(
					map((token: any) => {
						return { token };
					})
				);
			}),
			switchMap(({ token }) => {
				return forkJoin({
					userADProfile: this.profileService.getUserAdProfile(token),
					userADEmpId: this.profileService.getUserEmployeeId(token),
					userADManager: this.profileService.getUserAdDirectManager(token),
					userADDirectReports: this.profileService.getUserAdDirectReports(token),
				});
			}),
			switchMap((response) => {
				let userADProfile = response.userADProfile;
				let employeeNumber = response.userADEmpId?.value;
				let managerADProfile = response.userADManager;
				let managerADDirectReports: ManagerAdDirectReports[] = response.userADDirectReports.value.map((directReport: any) => ({
					email: directReport.mail,
					upn: directReport.userPrincipalName,
				}));

				/**
				 * If no active account set but there are accounts signed in, sets first account to active account
				 * To use active account set here, subscribe to inProgress$ first in your component
				 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
				 */
				let activeAccounts = this.authService.instance.getAllAccounts();
				let activeAccount = activeAccounts.length > 0 ? activeAccounts[0] : null;

				if (activeAccount && activeAccount.username.match(/adm@/i)) {
					const notification: UserNotification = {
						type: NotificationStates.Error,
						title: 'Invalid account',
						message: `The timesheet application does not support the use of the administrator emails. Please switch to your standard dnv.org account.`,
					};

					return of(ToastUserNotification({ notification }));
				} else {
					let newUser: NewUser = {
						firstName: userADProfile.givenName,
						lastName: userADProfile.surname,
						upn: userADProfile.userPrincipalName,
						email: userADProfile.mail,
						employeeNumber,
						createdAt: new Date(),
						isManager: activeAccount?.idTokenClaims?.roles?.includes(roles.Manager) || false,
						isTimekeeper: activeAccount?.idTokenClaims?.roles?.includes(roles.TimeKeepers) || false,
						isHR: activeAccount?.idTokenClaims?.roles?.includes(roles.HR) || false,
						managerEmail: managerADProfile.mail,
						managerUPN: managerADProfile.userPrincipalName,
						active: activeAccount?.idTokenClaims?.roles?.includes(roles.AppAccess) || false,
						managerDirectReports: managerADDirectReports,
					};

					return this.userService.addSingleUser(newUser).pipe(
						switchMap((response) => {
							console.log(response);

							const notification: UserNotification = {
								type: NotificationStates.Success,
								title: 'User profile successfully generated',
								message: `Your account profile successfully generated. Please wait while we re-load the web content.`,
							};

							return [ToastUserNotification({ notification }), LoadStaticData()];
						}),
						catchError((error) => {
							console.log(error);
							const notification: UserNotification = {
								type: NotificationStates.Error,
								title: 'Profile generation error',
								message: `Sorry, your profile failed to auto-generate. Please contact your manager to request your account be manually created.`,
							};

							return [ToastUserNotification({ notification })];
						})
					);
				}
			}),
			catchError((error) => {
				console.log(error);
				const notification: UserNotification = {
					type: NotificationStates.Error,
					title: 'Active directory query error',
					message: `Your account failed to be extracted from active directory. Please contact IT support if you believe this to be a mistake.`,
				};

				return [ToastUserNotification({ notification })];
			})
		)
	);

	getTimesheetHistorySuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetTimesheetHistorySuccess),

			withLatestFrom(this.store$),

			switchMap(([action, store]) => {
				// identify all the needed user images
				const requiredUserMap: { [id: string]: string } = {};
				const timesheets: Timesheet[] = action.success;
				timesheets.forEach((t: Timesheet) => {
					[t.sendToJDEBy, t.approvedBy, t.submittedBy, t.savedBy].forEach((upn: string | undefined) => {
						if (upn && upn !== 'timesheet@dnv.org') {
							requiredUserMap[upn] = upn;
						}
					});
				});

				// get missing user images
				const requiredUserImageUpn = Object.keys(requiredUserMap);

				// mergeMap((action: any) => {
				const account = (
					this.authService.instance.getActiveAccount()
						? this.authService.instance.getActiveAccount()
						: this.authService.instance.getAllAccounts().filter((a) => !a.username.match(/adm@/i))[0]
				)!;
				const request = {
					scopes: ['User.Read'],
					account,
				};

				return from(this.authService.instance.acquireTokenSilent(request)).pipe(
					map((token: any) => {
						return {
							token,
							requiredUserImageUpn,
						};
					})
				);
			}),
			switchMap(({ token, requiredUserImageUpn }) => {
				return forkJoin({
					blobs: forkJoin(
						requiredUserImageUpn.map((upn: string) => {
							return this.profileService.getTeamProfilePicObservable(upn, token.accessToken);
						})
					),
					requiredUserImageUpn: of(requiredUserImageUpn),
				});
			}),

			switchMap((response: any) => {
				// console.log(response);
				// const base64Img = this.convertBlobToBase64(response.teamPic);
				const blobMap: { [id: string]: SafeUrl } = {};

				response.requiredUserImageUpn.forEach((upn: string, index: number) => {
					const b: Blob | undefined = response.blobs[index];
					if (b) {
						const urlCreator = window.URL || window.webkitURL;
						const imgUrl = this.domSanitizer.bypassSecurityTrustUrl(urlCreator.createObjectURL(b));
						blobMap[upn] = imgUrl;
					}
				});

				return of(AddUserImages({ userImages: blobMap }));
			}),
			catchError((error: any, action: any) => {
				console.log(error);
				// 39 for where email starts after '.../users/...'
				// const slashIdx = error.url.substring(39).indexOf('/');
				// const email = error.url.substring(39).substring(0, slashIdx);
				return of(AddUserImages({ userImages: {} }));
			})
		)
	);

	getTimeBanks$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetTimeBanks),
			switchMap(() => {
				return this.timeBankService.getTimeBanks().pipe(map((data: TimeBank[]) => GetTimeBanksSuccess({ timeBank: data })));
			}),
			catchError((error) => of(GetTimeBanksError(error)))
		)
	);

	toastUserNotification$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(ToastUserNotification),
				map((action) => {
					const notification = action.notification;
					if (notification) {
						switch (notification.type) {
							case NotificationStates.Success:
								this.toastr.success(notification.message, notification.title, notification.options || {});
								break;

							case NotificationStates.Error:
								this.toastr.error(notification.message, notification.title, notification.options || {});
								break;

							case NotificationStates.Warning:
								this.toastr.warning(notification.message, notification.title, notification.options || {});
								break;

							case NotificationStates.Info:
								this.toastr.info(notification.message, notification.title, notification.options || {});
								break;

							default:
								const message =
									notification.message ||
									'Please raise a suport ticket with information about what you were doing in the application!';
								const title = notification.title || 'Something went wrong...';

								this.toastr.error(message, title, notification.options || {});
								break;
						}
					} else {
						this.toastr.error(
							'Please raise a suport ticket with information about what you were doing in the application!',
							'Something went wrong...',
							{}
						);
					}
				})
			),
		{ dispatch: false }
	);

	getNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GetNotifications),
			switchMap(() => {
				return this.notificationsService
					.getNotifications()
					.pipe(map((data: EmailNotification[]) => GetNotificationsSuccess({ notifications: data })));
			}),
			catchError((error) => of(GetNotificationsError(error)))
		)
	);

	updateNotification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateNotification),
			switchMap((action) => {
				return this.notificationsService.updateNotification(action.notification).pipe(
					map(() => {
						return UpdateNotificationSuccess();
					})
				);
			}),
			catchError((error) => of(UpdateNotificationError(error)))
		)
	);

	updateNotificationSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateNotificationSuccess),
			switchMap(() => {
				const notification: UserNotification = {
					type: NotificationStates.Success,
					title: '',
					message: `Email notification was updated successfully`,
				};
				return [GetNotifications(), ToastUserNotification({ notification })];
			}),
			catchError((error) => of(UpdateNotificationError(error)))
		)
	);

	updateNotificationActive$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateNotificationActive),
			switchMap((action) => {
				return this.notificationsService.updateNotificationActive(action.notification).pipe(
					map(() => {
						return UpdateNotificationSuccess();
					})
				);
			}),
			catchError((error) => of(UpdateNotificationError(error)))
		)
	);

	SaveMemberProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SaveMemberProfile),
			switchMap((action) => {
				return this.profileService.saveMemberProfile(action.config).pipe(
					switchMap((response) => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Profile saved',
							message: `Team member profile was successfully saved.`,
						};

						const actions: Action[] = [
							ToastUserNotification({ notification }),
							SaveMemberProfileSuccess({ manage: response as ManageProfile[] }),
						];

						return actions;
					})
				);
			}),
			catchError((error) => {
				// console.log(error);
				const notification: UserNotification = {
					type: NotificationStates.Error,
					title: 'Profile Save Error',
					message: `Team member profile failed to save.`,
				};

				const actions: Action[] = [ToastUserNotification({ notification }), SaveMemberProfileError(error)];

				return actions;
			})
		)
	);

	UpdateMemberProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UpdateMemberProfile),
			switchMap((action) => {
				return this.profileService.updateMemberProfile(action.updateConfig).pipe(
					switchMap((response) => {
						const notification: UserNotification = {
							type: NotificationStates.Success,
							title: 'Profile Updated',
							message: `Team member profile was successfully updated.`,
						};

						const actions: Action[] = [
							ToastUserNotification({ notification }),
							UpdateMemberProfileSuccess({
								manage: response as ManageProfile[],
							}),
						];

						return actions;
					})
				);
			}),
			catchError((error) => {
				console.log(error);
				const notification: UserNotification = {
					type: NotificationStates.Error,
					title: 'Profile Save Error',
					message: `Team member profile failed to update.`,
				};

				const actions: Action[] = [ToastUserNotification({ notification }), UpdateMemberProfileError(error)];

				return actions;
			})
		)
	);

	convertBlobToBase64 = (blob: Blob) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onerror = reject;
			reader.onload = () => {
				resolve(reader.result);
			};
			reader.readAsDataURL(blob);
		});

	constructor(
		private actions$: Actions,
		private store$: Store<State>,
		private userService: UserService,
		private profileService: ProfileService,
		private authService: MsalService,
		private payPeriodService: PayPeriodService,
		private actingService: ActingService,
		private delegationService: DelegationsService,
		private entryService: EntryService,
		private timesheetService: TimesheetService,
		private statService: StatService,
		private staticService: StaticService,
		private timeBankService: TimebankService,
		private toastr: ToastrService,
		private modalService: NgbModal,
		private notificationsService: NotificationsService,
		private domSanitizer: DomSanitizer
	) {}
}
